import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { apiService } from "@/services/apiService";
import { useAuthStore } from "@/stores/auth";
import { PERMISSIONS } from "@/constants/permissions";

export function useOrder() {
  const { t } = useI18n();
  const { hasScope } = useAuthStore();

  const error = ref(null);
  const loading = ref(false);
  const orders = ref([]);

  const canReadOrder = ref(hasScope(PERMISSIONS.ORDER_READ));
  const canCreateOrder = ref(hasScope(PERMISSIONS.ORDER_CREATE));
  const canEditOrder = ref(hasScope(PERMISSIONS.ORDER_EDIT));

  const fetchOrders = async (skip = 0, limit = 100) => {
    if (!canReadOrder.value) {
      const errorObject = new Error(t("notAllowed"));
      errorObject.detail = t("crudMessages.readDenied", {
        resource: "orders",
      });
      error.value = errorObject.detail;
      return [];
    }
    loading.value = true;
    error.value = null;
    try {
      const response = await apiService.get("/orders/", {
        skip,
        limit,
      });

      orders.value = Array.isArray(response) ? response : [];
      return orders.value;
    } catch (err) {
      error.value =
        err?.detail ||
        err?.message ||
        t("crudMessages.fetchError", { resource: "orders" });
      orders.value = [];
      return orders.value;
    } finally {
      loading.value = false;
    }
  };

  const createOrder = async (orderData) => {
    if (!canCreateOrder.value) {
      const errorObject = new Error(t("notAllowed"));
      errorObject.detail = t("crudMessages.writeDenied", {
        resource: "order",
      });
      error.value = errorObject.detail;
      return null;
    }
    loading.value = true;
    error.value = null;
    try {
      const newOrder = await apiService.post("/orders/", orderData);
      orders.value.push(newOrder);
      return newOrder;
    } catch (err) {
      error.value =
        err?.detail ||
        err?.message ||
        t("crudMessages.createError", { resource: "order" });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const updateOrderStatus = async (orderId, statusData) => {
    if (!canEditOrder.value) {
      const errorObject = new Error(t("notAllowed"));
      errorObject.detail = t("crudMessages.writeDenied", {
        resource: "order",
      });
      error.value = errorObject.detail;
      return null;
    }
    loading.value = true;
    error.value = null;
    try {
      const updatedOrder = await apiService.put(
        `/orders/${orderId}/status`,
        statusData
      );
      const index = orders.value.findIndex((o) => o.id === orderId);
      if (index !== -1) {
        orders.value[index] = updatedOrder;
      }
      return updatedOrder;
    } catch (err) {
      error.value =
        err?.detail ||
        err?.message ||
        t("crudMessages.updateError", { resource: "order" });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const cancelOrder = async (orderId, reason) => {
    if (!canEditOrder.value) {
      const errorObject = new Error(t("notAllowed"));
      errorObject.detail = t("crudMessages.writeDenied", {
        resource: "order",
      });
      error.value = errorObject.detail;
      return null;
    }
    loading.value = true;
    error.value = null;
    try {
      const updatedOrder = await apiService.put(
        `/orders/${orderId}/cancel`,
        reason
      );
      const index = orders.value.findIndex((o) => o.id === orderId);
      if (index !== -1) {
        orders.value[index] = updatedOrder;
      }
      return updatedOrder;
    } catch (err) {
      error.value =
        err?.detail ||
        err?.message ||
        t("crudMessages.updateError", { resource: "order" });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const recreateOrder = async (orderId) => {
    if (!canEditOrder.value) {
      const errorObject = new Error(t("notAllowed"));
      errorObject.detail = t("crudMessages.writeDenied", {
        resource: "order",
      });
      error.value = errorObject.detail;
      return null;
    }
    loading.value = true;
    error.value = null;
    try {
      const recreatedOrder = await apiService.put(
        `/orders/${orderId}/recreate`
      );
      const index = orders.value.findIndex((o) => o.id === orderId);
      if (index !== -1) {
        orders.value[index] = recreatedOrder;
      } else {
        orders.value.push(recreatedOrder);
      }
      return recreatedOrder;
    } catch (err) {
      error.value =
        err?.detail ||
        err?.message ||
        t("crudMessages.updateError", { resource: "order" });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const overrideOrderPayment = async (orderId, paymentData) => {
    if (!canEditOrder.value) {
      const errorObject = new Error(t("notAllowed"));
      errorObject.detail = t("crudMessages.writeDenied", {
        resource: "order",
      });
      error.value = errorObject.detail;
      return null;
    }
    loading.value = true;
    error.value = null;
    try {
      const updatedOrder = await apiService.put(
        `/orders/${orderId}/override-payment`,
        paymentData
      );
      const index = orders.value.findIndex((o) => o.id === orderId);
      if (index !== -1) {
        orders.value[index] = updatedOrder;
      }
      return updatedOrder;
    } catch (err) {
      error.value =
        err?.detail ||
        err?.message ||
        t("crudMessages.updateError", { resource: "order" });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const applyDiscount = async (orderId, productId, discountData) => {
    if (!canEditOrder.value) {
      const errorObject = new Error(t("notAllowed"));
      errorObject.detail = t("crudMessages.writeDenied", {
        resource: "order",
      });
      error.value = errorObject.detail;
      return null;
    }
    loading.value = true;
    error.value = null;
    try {
      const updatedOrder = await apiService.put(
        `/orders/${orderId}/apply-discount/${productId}`,
        discountData
      );
      const index = orders.value.findIndex((o) => o.id === orderId);
      if (index !== -1) {
        orders.value[index] = updatedOrder;
      }
      return updatedOrder;
    } catch (err) {
      error.value =
        err?.detail ||
        err?.message ||
        t("crudMessages.updateError", { resource: "order" });
      throw err;
    } finally {
      loading.value = false;
    }
  };

  const getAvailableOrderTypes = () => {
    return [
      { value: "dine_in", label: "dineIn" },
      { value: "take_out", label: "takeOut" },
    ];
  };

  const getPaymentMethodString = (type) => {
    switch (type) {
      case "cash":
        return "Cash";
      case "card":
        return "Card";
      case "promptpay":
        return "Prompt Pay";
      default:
        return "";
    }
  };

  const getOrderTypeString = (type) => {
    switch (type) {
      case "dine_in":
      case "dineIn":
        return "Dine In";
      case "take_out":
      case "takeOut":
        return "Take Out";
      default:
        return "";
    }
  };

  const isSameOrderItem = (a, b) => {
    return (
      a.food.id === b.food.id &&
      JSON.stringify(normalizeOptions(a.options)) ===
        JSON.stringify(normalizeOptions(b.options)) &&
      JSON.stringify(normalizeExtras(a.extras)) ===
        JSON.stringify(normalizeExtras(b.extras))
    );
  };

  const normalizeExtras = (extras) => {
    return [...extras].sort((a, b) => {
      if (a.id !== b.id) return a.id - b.id;
      return a.option.localeCompare(b.option);
    });
  };

  const normalizeOptions = (options) => {
    return Object.entries(options)
      .sort(([nameA], [nameB]) => nameA.localeCompare(nameB))
      .map(([name, option]) => ({ name, option: option.option }));
  };

  return {
    applyDiscount,
    cancelOrder,
    canCreateOrder,
    canEditOrder,
    canReadOrder,
    createOrder,
    error,
    fetchOrders,
    getAvailableOrderTypes,
    getOrderTypeString,
    getPaymentMethodString,
    isSameOrderItem,
    loading,
    normalizeExtras,
    normalizeOptions,
    overrideOrderPayment,
    orders,
    recreateOrder,
    updateOrderStatus,
  };
}
